import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { NAV_MENU } from '../../core/constants/menu.constants';
import { NavGroup, NavItem } from '../../core/models/nav.model';
import { APP } from '../../core/constants/app.constants';
import { AuthService } from '../../core/auth/auth.service';
import { NavigationService } from '../../core/services/navigation.service';
import { GetterSetterService } from '../../core/services/getter-setter.service';
import { PermissionService } from '../../core/services/permission.service';
@Component({
	selector: 'dmp-sidebar',
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false
})
export class SidebarComponent implements OnInit, OnDestroy {
	navMenu: NavGroup[] = [];
	collapsed = false;
	hoveredItem: NavItem | null = null;
	flyoutTop = 0;
	/** Stored session profile from GetterSetterService (`userDetails`). */
	userDetails: Record<string, unknown> = {};
	private readonly destroy$ = new Subject<void>();
	constructor(
		private router: Router,
		private navService: NavigationService,
		private authService: AuthService,
		private getSetService: GetterSetterService,
		private permissionService: PermissionService,
	) { }
	get displayName(): string {
		const firstName = this.readUserField('firstName');
		return firstName ? `@${firstName}` : APP.USER.NAME;
	}
	get displayRole(): string {
		const role = this.readUserField('role');
		if (role) return role;
		const roleNames = this.userDetails['roleNames'];
		if (Array.isArray(roleNames) && roleNames.length) {
			return roleNames.filter((n): n is string => typeof n === 'string' && !!n.trim()).join(', ');
		}
		return APP.USER.ROLE;
	}
	get displayInitials(): string {
		return this.readUserField('initials') || APP.USER.INITIALS;
	}
	ngOnInit(): void {
		this.refreshMenu();
		this.refreshUserDetails();
		this.authService.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshUserDetails());
		this.permissionService.permissions$.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshMenu());
		this.permissionService.permissionScopes$.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshMenu());
		this.collapsed = this.navService.isCollapsed;
		this.navService.sidebarCollapsed$.pipe(takeUntil(this.destroy$)).subscribe(v => {
			this.collapsed = v;
			if (!v) {
				this.hoveredItem = null;
			}
		});
	}
	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
	isActive(href: string): boolean {
		const url = this.router.url.split(/[?#]/)[0];
		if (href === '/') return url === '/';
		return this.bestMatchHref(url) === href;
	}
	private bestMatchHref(url: string): string | null {
		let best: string | null = null;
		for (const group of this.navMenu) {
			for (const item of group.items) {
				const href = item.href;
				const matches = href === '/' ? url === '/' : url === href || url.startsWith(href + '/');
				if (matches && (best === null || href.length > best.length)) {
					best = href;
				}
			}
		}
		return best;
	}
	navigate(href: string): void {
		this.router.navigate([href]);
		this.navService.closeSidebar();
		this.clearFlyout();
	}
	onItemPointerEnter(event: Event, item: NavItem): void {
		if (!this.collapsed) return;
		const el = event.currentTarget as HTMLElement;
		this.flyoutTop = el.getBoundingClientRect().top;
		this.hoveredItem = item;
	}
	onItemPointerLeave(): void {
		this.clearFlyout();
	}
	onItemFocus(event: FocusEvent, item: NavItem): void {
		if (!this.collapsed) return;
		const el = event.currentTarget as HTMLElement;
		this.flyoutTop = el.getBoundingClientRect().top;
		this.hoveredItem = item;
	}
	onItemBlur(): void {
		this.clearFlyout();
	}
	private refreshUserDetails(): void {
		const details = this.getSetService.getUserDetails();
		this.userDetails = details && typeof details === 'object' && !Array.isArray(details) ? details as Record<string, unknown> : {};
	}
	private refreshMenu(): void {
		this.navMenu = this.permissionService.filterMenu(NAV_MENU);
	}
	private readUserField(key: string): string {
		const value = this.userDetails?.[key];
		return typeof value === 'string' && value.trim() ? value.trim() : '';
	}
	private clearFlyout(): void {
		this.hoveredItem = null;
	}
}
